import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { DatePicker } from 'antd';
import { LineChart, Star } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { dayjs } from '@/lib/dayjs';
import type { SensorWithContext, TelemetryQuery } from '@monitor/shared';
import { usePhaseSeries } from '@/features/telemetry/hooks';
import type { PhaseSeriesPoint } from '@/features/telemetry/api';

// ─── metric + range config ──────────────────────────────────────────────────
type MetricKey = 'power' | 'voltage' | 'current' | 'energy' | 'temperature';
const METRICS: Record<MetricKey, { label: string; unit: string; digits: number; perPhase: boolean; total: boolean }> = {
  power:       { label: 'กำลังไฟ', unit: 'W',   digits: 1, perPhase: true,  total: true },
  voltage:     { label: 'แรงดัน',  unit: 'V',   digits: 1, perPhase: true,  total: false },
  current:     { label: 'กระแส',   unit: 'A',   digits: 3, perPhase: true,  total: false },
  energy:      { label: 'พลังงาน', unit: 'kWh', digits: 3, perPhase: false, total: false },
  temperature: { label: 'อุณหภูมิ', unit: '°C', digits: 1, perPhase: false, total: false },
};

type RangeKey = 'realtime' | '24h' | '7d' | '30d' | 'custom';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'realtime', label: 'เรียลไทม์' },
  { key: '24h', label: '24 ชม.' },
  { key: '7d', label: '7 วัน' },
  { key: '30d', label: '30 วัน' },
  { key: 'custom', label: 'กำหนดเอง' },
];
// map UI range → backend TimeRangeDto value
const RANGE_API: Record<RangeKey, TelemetryQuery['range']> = { realtime: 'realtime', '24h': '24h', '7d': '7d', '30d': 'month', custom: 'custom' };

// standard 3-phase colours: L1 red, L2 yellow, L3 blue, Total grey
const PHASE = { a: '#ef4444', b: '#facc15', c: '#3b82f6', total: '#cbd5e1' };
const CMP = { pzem: '#06b6d4', kws: '#a78bfa' };

const isPzem = (s: SensorWithContext) => (s.model ?? '').toLowerCase().startsWith('pzem');
const isKws = (s: SensorWithContext) => (s.model ?? '').toLowerCase().startsWith('kws');
const is3ph = (s?: SensorWithContext) =>
  !!s && (s.phases === 3 || (s.phases == null && /AC306|3P|THREE/i.test(s.model ?? '')));

const pill = (active: boolean): React.CSSProperties => ({
  padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
  background: active ? 'var(--cyan)' : 'transparent', color: active ? '#001' : 'var(--dim)',
  fontWeight: active ? 700 : 500, fontSize: 12,
});
const pillWrap: React.CSSProperties = {
  display: 'inline-flex', gap: 4, flexWrap: 'wrap', background: 'var(--bg-input)',
  padding: 3, borderRadius: 9, border: '1px solid var(--border-color)',
};

export function EnergyHistory({ siteId, sensors }: { siteId: number; sensors: SensorWithContext[] }) {
  const pzem = sensors.find(isPzem);
  const kws = sensors.find(isKws);
  const bothConnected = !!pzem && !!kws;
  const primary = sensors.find((s) => s.isPrimary) ?? kws ?? pzem;

  // view = a sensorId, or 'compare'. Default = the primary sensor.
  const [view, setView] = useState<number | 'compare'>(primary?.id ?? pzem?.id ?? kws?.id ?? 0);
  const [metric, setMetric] = useState<MetricKey>('power');
  const [range, setRange] = useState<RangeKey>('24h');
  // Store the custom range as stable Dayjs objects. Re-deriving them from a
  // string on every render gave the RangePicker fresh object identities mid-
  // interaction, which reset the selection ("bouncing").
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(2, 'day'), dayjs()]);
  const [cFrom, cTo] = customRange;

  const viewSensor = view === 'compare' ? undefined : sensors.find((s) => s.id === view);
  const compareMode = view === 'compare';

  // temperature only exists on KWS; hide the tab otherwise + reset if selected
  const tempAvailable = !compareMode && !!viewSensor && isKws(viewSensor);
  const metricList = (Object.keys(METRICS) as MetricKey[]).filter((k) => k !== 'temperature' || tempAvailable);
  const activeMetric: MetricKey = metric === 'temperature' && !tempAvailable ? 'power' : metric;
  const m = METRICS[activeMetric];

  const customValid = range !== 'custom'
    || (cTo.isAfter(cFrom) && cTo.diff(cFrom, 'day') <= 90);

  const mkQuery = (sensorId: number) => ({
    siteId,
    sensorId,
    metric: activeMetric,
    range: RANGE_API[range],
    ...(range === 'custom' && customValid
      ? { from: cFrom.toISOString(), to: cTo.toISOString() }
      : {}),
  });

  // A = the single/selected sensor (or PZEM in compare); B = KWS in compare only.
  const idA = compareMode ? pzem?.id : viewSensor?.id;
  const idB = compareMode ? kws?.id : undefined;
  const qA = usePhaseSeries(idA != null && (range !== 'custom' || customValid) ? mkQuery(idA) : null);
  const qB = usePhaseSeries(idB != null && (range !== 'custom' || customValid) ? mkQuery(idB) : null);

  const live = range === 'realtime';
  const dayBucket = range === '7d' || range === '30d' || (range === 'custom' && cTo.diff(cFrom, 'day') > 2);
  // A custom hourly range that crosses midnight needs the date on the label —
  // otherwise every midnight bucket just reads "00:00" and the days are
  // indistinguishable. Daily buckets show DD/MM; a same-day range shows HH:mm.
  const customMultiDayHourly = range === 'custom' && !dayBucket && cTo.diff(cFrom, 'day') >= 1;
  const xfmt = dayBucket ? 'DD/MM' : customMultiDayHourly ? 'DD/MM HH:mm' : 'HH:mm';
  const fmtX = (iso: string) => dayjs(iso).format(xfmt);

  // Show the FULL selected window as-is — no trimming. The x-axis must span
  // exactly the chosen range (start → end) even where there's no data, so gaps
  // read as gaps rather than the axis silently shrinking to the populated part.
  const keep = (pts: PhaseSeriesPoint[] | undefined): PhaseSeriesPoint[] => pts ?? [];

  const ptsA = keep(qA.data?.points);
  const ptsB = compareMode ? keep(qB.data?.points) : [];
  const loading = qA.isLoading || (compareMode && qB.isLoading);
  const threePhase = is3ph(viewSensor);

  const option = useMemo(() => {
    // choose the axis from whichever series has more points (compare buckets align)
    const axis = (ptsA.length >= ptsB.length ? ptsA : ptsB).map((p) => fmtX(p.time));
    type S = { name: string; data: (number | null)[]; color: string; width: number; dashed?: boolean };
    const series: S[] = [];

    if (compareMode) {
      series.push({ name: pzem?.code ?? 'PZEM', data: ptsA.map((p) => p.val), color: CMP.pzem, width: 2 });
      const kwsLbl = (kws?.code ?? 'KWS') + (is3ph(kws) ? ' (รวม 3φ)' : '');
      series.push({ name: kwsLbl, data: ptsB.map((p) => p.val), color: CMP.kws, width: 2, dashed: true });
    } else if (threePhase && m.perPhase) {
      series.push({ name: 'L1', data: ptsA.map((p) => p.a), color: PHASE.a, width: 1.5 });
      series.push({ name: 'L2', data: ptsA.map((p) => p.b), color: PHASE.b, width: 1.5 });
      series.push({ name: 'L3', data: ptsA.map((p) => p.c), color: PHASE.c, width: 1.5 });
      if (m.total) series.push({ name: 'รวม', data: ptsA.map((p) => p.val), color: PHASE.total, width: 3 });
    } else {
      series.push({ name: m.label, data: ptsA.map((p) => p.val), color: CMP.pzem, width: 2 });
    }

    return {
      grid: { left: 54, right: 16, top: 34, bottom: 28 },
      legend: {
        // ECharts renders to <canvas>, which cannot resolve CSS variables —
        // colors MUST be literal hex/rgb or the text falls back to near-black
        // and vanishes on the dark background.
        show: series.length > 1, top: 2, textStyle: { color: '#cbd5e1', fontSize: 11 },
        icon: 'roundRect', itemWidth: 14, itemHeight: 8,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(17,23,41,0.95)', borderColor: 'rgba(148,163,184,0.2)',
        textStyle: { color: '#f1f5f9', fontSize: 12 },
        valueFormatter: (v: number | null) => (v == null ? '—' : `${Number(v).toFixed(m.digits)} ${m.unit}`),
      },
      xAxis: {
        type: 'category', data: axis, boundaryGap: false,
        axisLabel: { color: '#94a3b8', fontSize: 10, hideOverlap: true },
        axisLine: { lineStyle: { color: 'rgba(148,163,184,0.35)' } }, splitLine: { show: false },
      },
      yAxis: {
        // Always anchor at 0 — auto-scaling zoomed into the data band and made
        // small variations look dramatic / values look artificially low.
        type: 'value', name: m.unit, min: 0,
        nameTextStyle: { color: '#cbd5e1', fontSize: 10 },
        axisLabel: { color: '#94a3b8', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(148,163,184,0.14)' } },
      },
      series: series.map((s) => ({
        name: s.name, type: 'line', smooth: true, symbol: 'none', sampling: 'lttb', connectNulls: false,
        data: s.data,
        lineStyle: { color: s.color, width: s.width, type: s.dashed ? 'dashed' : 'solid' },
        itemStyle: { color: s.color },
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptsA, ptsB, compareMode, threePhase, activeMetric, m, pzem?.code, kws?.code]);

  const hasData = ptsA.some((p) => p.val != null || p.a != null) || ptsB.some((p) => p.val != null);

  return (
    <section className="card fade-in-up" style={{ padding: 20 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <LineChart size={15} color="var(--cyan)" />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          ประวัติพลังงาน
        </div>
        {live && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#22c55e', fontWeight: 700 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} /> LIVE
          </span>
        )}
      </div>

      {/* sensor selector */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={pillWrap}>
          {pzem && (
            <button style={pill(view === pzem.id)} onClick={() => setView(pzem.id)}>
              {pzem.isPrimary && <Star size={11} fill="currentColor" style={{ verticalAlign: '-1px', marginRight: 3 }} />}PZEM
            </button>
          )}
          {kws && (
            <button style={pill(view === kws.id)} onClick={() => setView(kws.id)}>
              {kws.isPrimary && <Star size={11} fill="currentColor" style={{ verticalAlign: '-1px', marginRight: 3 }} />}KWS{is3ph(kws) ? ' 3φ' : ''}
            </button>
          )}
          {bothConnected && (
            <button style={pill(view === 'compare')} onClick={() => setView('compare')}>เปรียบเทียบ</button>
          )}
        </div>
      </div>

      {/* metric tabs + range */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, justifyContent: 'space-between' }}>
        <div style={pillWrap}>
          {metricList.map((k) => (
            <button key={k} style={pill(activeMetric === k)} onClick={() => setMetric(k)}>{METRICS[k].label}</button>
          ))}
        </div>
        <div style={pillWrap}>
          {RANGES.map((r) => (
            <button key={r.key} style={pill(range === r.key)} onClick={() => setRange(r.key)}>{r.label}</button>
          ))}
        </div>
      </div>

      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="DD/MM/YYYY HH:mm"
            value={customRange}
            onChange={(v) => { if (v && v[0] && v[1]) setCustomRange([v[0], v[1]]); }}
            disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
            allowClear={false}
            placeholder={['เริ่มต้น', 'สิ้นสุด']}
          />
          {!customValid && <span style={{ color: '#ef4444', fontSize: 11 }}>เลือกช่วง 1–90 วัน (เริ่มก่อนสิ้นสุด)</span>}
        </div>
      )}

      {/* summary cards */}
      <SummaryRow
        m={m}
        a={stats(ptsA)}
        b={compareMode ? stats(ptsB) : null}
        labelA={compareMode ? pzem?.code ?? 'PZEM' : undefined}
        labelB={compareMode ? kws?.code ?? 'KWS' : undefined}
      />

      {/* chart */}
      <div style={{ position: 'relative', minHeight: 300 }}>
        {loading && !hasData ? (
          <Empty text="กำลังโหลด..." />
        ) : !hasData ? (
          <Empty text="ไม่มีข้อมูลในช่วงเวลานี้" />
        ) : (
          <ReactECharts option={option} style={{ height: 320 }} notMerge lazyUpdate />
        )}
      </div>

      {metric === 'energy' && threePhase && !compareMode && (
        <div style={{ fontSize: 11, color: 'var(--dim2)', marginTop: 6 }}>
          * พลังงานแสดงค่ารวมเท่านั้น (มิเตอร์ไม่ได้แยกพลังงานรายเฟส)
        </div>
      )}
    </section>
  );
}

type Stat = { avg: number; max: number; min: number } | null;
function stats(pts: PhaseSeriesPoint[]): Stat {
  const vals = pts.map((p) => p.val).filter((v): v is number => v != null);
  if (!vals.length) return null;
  const maxes = pts.map((p) => p.max ?? p.val).filter((v): v is number => v != null);
  const mins = pts.map((p) => p.min ?? p.val).filter((v): v is number => v != null);
  return {
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    max: Math.max(...maxes),
    min: Math.min(...mins),
  };
}

function SummaryRow({
  m, a, b, labelA, labelB,
}: {
  m: { unit: string; digits: number };
  a: Stat; b: Stat; labelA?: string; labelB?: string;
}) {
  const fmt = (v: number) => `${v.toFixed(m.digits)} ${m.unit}`;
  const delta = a && b && b.avg !== 0 ? ((a.avg - b.avg) / Math.abs(b.avg)) * 100 : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
      <Stat label={labelA ? `เฉลี่ย · ${labelA}` : 'เฉลี่ย'} value={a ? fmt(a.avg) : '—'} color="var(--cyan)" />
      <Stat label="สูงสุด" value={a ? fmt(a.max) : '—'} color="#22c55e" />
      <Stat label="ต่ำสุด" value={a ? fmt(a.min) : '—'} color="#f59e0b" />
      {b && <Stat label={`เฉลี่ย · ${labelB}`} value={fmt(b.avg)} color="#a78bfa" />}
      {delta != null && (
        <Stat
          label="ส่วนต่าง (เฉลี่ย)"
          value={`${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`}
          color={Math.abs(delta) < 5 ? 'var(--dim)' : delta >= 0 ? '#22c55e' : '#ef4444'}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '9px 12px' }}>
      <div style={{ fontSize: 10, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} /> {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dim)', fontSize: 13 }}>
      {text}
    </div>
  );
}
